import { useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { Timestamp } from 'api/models';
import { Calendars, getCalendars } from 'api/timesheets/integration/calendar';
import {
  CalendarEventsWithMeta,
  CalenderEventApi,
} from 'api/timesheets/integration/event';
import { CalendarEventsParam } from 'api/timesheets/integration/utils';
import { useGetProjectIdsOfCurrentUserFromIndexedDb } from 'hooks/use-idb';

const getBaseQueryKey = () => ['timesheet-calendar-integration'];
const getIntegrationQueryKey = (integrationId: number) => [
  ...getBaseQueryKey(),
  integrationId,
];

const getCalenderQueryKey = (integrationId: number) => [
  ...getIntegrationQueryKey(integrationId),
  'calender',
];

export const useTimesheetCalendarQuery = (
  integrationId?: number,
): UseQueryResult<Calendars> => {
  return useQuery(
    getCalenderQueryKey(integrationId!),
    () => getCalendars(integrationId!),
    {
      enabled: !!integrationId,
    },
  );
};

const getEventQueryKey = (
  integrationId: number,
  calenderId: string,
  filter: { startDate?: Timestamp; endDate?: Timestamp },
) => [
  ...getCalenderQueryKey(integrationId),
  calenderId,
  JSON.stringify(filter),
];

const getCalenderEventsWithMyProjects = (
  params: CalendarEventsParam & { myProjects: number[] },
) => {
  const addMyProjectsToEvents = (event) => ({
    ...event,
    myProjectIds: params.myProjects,
  });

  return CalenderEventApi.getCalenderEvents(params).then(
    (eventWithMeta): CalendarEventsWithMeta => {
      return eventWithMeta.map(([meta, event]) => {
        return [meta, event.map(addMyProjectsToEvents)];
      });
    },
  );
};

export const useTimesheetCalendarEventsQuery = ({
  integrationId,
  calendarId: calenderId,
  startDate,
  endDate,
}: AtLeast<
  CalendarEventsParam,
  'integrationId'
>): UseQueryResult<CalendarEventsWithMeta> => {
  const myProjectIds = useGetProjectIdsOfCurrentUserFromIndexedDb();

  return useQuery(
    getEventQueryKey(integrationId, calenderId!, { startDate, endDate }),
    () =>
      getCalenderEventsWithMyProjects({
        integrationId,
        calendarId: calenderId!,
        startDate: startDate!,
        endDate: endDate!,
        myProjects: myProjectIds,
      }),
    {
      enabled: Boolean(integrationId && calenderId && startDate && endDate),
      retryOnMount: true,
    },
  );
};

export const useInvalidateCalendarIntegration = () => {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries(getBaseQueryKey());
  };
};
